import React from "react";
import { CheckCircle2, ExternalLink, Loader2, ShieldCheck } from "lucide-react";
import { AppButton, AppModal, Callout } from "../ui";
import { formatCurrency } from "../../utils/format";

/**
 * HostedCheckoutModal — what this tab shows while a platform-billed payment is
 * being made in the new tab `useHostedCheckout` opened (custom domains only;
 * see utils/hostedCheckout).
 *
 * It says where the payment is happening, offers a link back to that tab, and
 * changes by itself when the payment lands. Closing it cancels nothing — a
 * payment completed in the other tab is recorded either way — and it says so,
 * because "I closed the box, did I lose my money?" is the question it invites.
 */

const TITLES = {
  opening: "Opening secure payment",
  waiting: "Finish paying in the new tab",
  blocked: "Open the payment page",
  paid: "Payment received",
  expired: "Payment page expired",
  closed: "Payment page closed",
  error: "Could not start the payment",
};

const CLOSED_COPY = {
  superseded: "This payment was started again in another tab. Finish it there, or start again from this page.",
  already_paid: "This has already been paid. Nothing more is due.",
  unavailable: "This can no longer be paid online — it was cancelled or changed. Refresh the page to see where it stands.",
};

const amountOf = (session) => (session?.amount != null ? formatCurrency(session.amount / 100) : "");

function Summary({ session }) {
  if (!session) return null;
  return (
    <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-3 tw-rounded-md tw-border tw-border-solid tw-border-line tw-px-3 tw-py-2.5">
      <span className="tw-text-sm tw-text-content">{session.title}</span>
      <strong className="tw-whitespace-nowrap tw-text-sm tw-text-content">{amountOf(session)}</strong>
    </div>
  );
}

function OpenPaymentLink({ url, variant, children }) {
  return (
    <AppButton as="a" href={url} target="_blank" rel="noopener noreferrer" variant={variant}>
      {children}
      <ExternalLink size={14} aria-hidden="true" />
    </AppButton>
  );
}

export default function HostedCheckoutModal({ checkout }) {
  const { phase, session, url, message, checking, checkNow, dismiss } = checkout;
  if (!phase || phase === "idle") return null;

  let body = null;
  let footer = null;

  switch (phase) {
    case "opening":
      body = (
        <p className="tw-m-0 tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-content-muted" role="status">
          <Loader2 size={16} className="tw-animate-spin" aria-hidden="true" />
          Opening the secure payment page…
        </p>
      );
      break;

    case "waiting":
    case "blocked":
      body = (
        <div className="tw-flex tw-flex-col tw-gap-3">
          <Summary session={session} />
          {phase === "blocked" ? (
            <Callout tone="warning" role="alert">
              Your browser blocked the new tab. Open the payment page with the button below.
            </Callout>
          ) : (
            <p className="tw-m-0 tw-text-sm tw-text-content">
              The payment opened in a new tab on our secure payment page. This window updates by itself as soon as
              the payment goes through.
            </p>
          )}
          {session?.lastError && (
            <Callout tone="danger">The last attempt did not go through: {session.lastError}</Callout>
          )}
          <p className="tw-m-0 tw-text-xs tw-text-content-muted">
            Closing this window cancels nothing — a payment you complete in the other tab is still recorded.
          </p>
        </div>
      );
      footer =
        phase === "blocked" ? (
          <OpenPaymentLink url={url} variant="default">Open payment page</OpenPaymentLink>
        ) : (
          <>
            <OpenPaymentLink url={url} variant="outline">Open payment page again</OpenPaymentLink>
            <AppButton onClick={checkNow} disabled={checking}>
              {checking ? "Checking…" : "I've paid — check now"}
            </AppButton>
          </>
        );
      break;

    case "paid":
      body = (
        <div className="tw-flex tw-items-start tw-gap-3" role="status">
          <CheckCircle2 size={24} className="tw-shrink-0 tw-text-success" aria-hidden="true" />
          <p className="tw-m-0 tw-text-sm tw-text-content">
            {session?.title} — {amountOf(session)} is paid. A receipt is on its way by email.
          </p>
        </div>
      );
      footer = <AppButton onClick={dismiss}>Done</AppButton>;
      break;

    case "expired":
      body = (
        <Callout tone="warning">
          The payment page expired before a payment went through, so nothing was charged. Start again when you are
          ready.
        </Callout>
      );
      footer = <AppButton variant="outline" onClick={dismiss}>Close</AppButton>;
      break;

    case "closed":
      body = <Callout tone="info">{CLOSED_COPY[session?.closedReason] || CLOSED_COPY.unavailable}</Callout>;
      footer = <AppButton variant="outline" onClick={dismiss}>Close</AppButton>;
      break;

    case "error":
    default:
      body = <Callout tone="danger" role="alert">{message || "Could not start the payment. Please try again."}</Callout>;
      footer = <AppButton variant="outline" onClick={dismiss}>Close</AppButton>;
      break;
  }

  return (
    <AppModal
      show
      onHide={dismiss}
      title={TITLES[phase] || TITLES.error}
      icon={<ShieldCheck size={18} aria-hidden="true" />}
      size="sm"
      closeOnOverlay={false}
      footer={footer}
    >
      {body}
    </AppModal>
  );
}
